// src/pages/SupplierPrograms.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../contexts/UserContext';
import { useSupplierPrograms } from '../contexts/SupplierProgramsContext';
import CreateProgramModal from '../components/modals/CreateProgramModal';
import ChangeProgramStatusModal from '../components/modals/ChangeProgramStatusModal';
import ProgramDescriptionModal from '../components/modals/ProgramDescriptionModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import SupplierProgramsHeader from '../components/ui/SupplierProgramsHeader';

const STATUS_LABELS = {
  DRAFT: 'טיוטה',
  PENDING: 'ממתין לאישור',
  APPROVED: 'מאושר',
  ACTIVE: 'פעיל',
  SUSPENDED: 'מושעה',
  CLOSED: 'סגור',
};

const STATUS_BADGE_CLASS = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-emerald-50 text-emerald-700',
  ACTIVE: 'bg-emerald-50 text-emerald-700',
  SUSPENDED: 'bg-red-50 text-red-600',
  CLOSED: 'bg-gray-100 text-gray-500',
};

const STATUS_DOT_CLASS = {
  DRAFT: 'bg-gray-400',
  PENDING: 'bg-amber-500',
  APPROVED: 'bg-emerald-500',
  ACTIVE: 'bg-emerald-500',
  SUSPENDED: 'bg-red-500',
  CLOSED: 'bg-gray-400',
};

const STATUS_BAR_CLASS = {
  DRAFT: 'bg-gray-300',
  PENDING: 'bg-amber-400',
  APPROVED: 'bg-emerald-400',
  ACTIVE: 'bg-emerald-400',
  SUSPENDED: 'bg-red-400',
  CLOSED: 'bg-gray-300',
};

const TAB_LIST = [
  { key: 'ALL', label: 'הכל' },
  { key: 'ACTIVE', label: 'פעילות', statuses: ['APPROVED', 'ACTIVE'] },
  { key: 'PENDING', label: 'ממתינות', statuses: ['PENDING'] },
  { key: 'DRAFT', label: 'טיוטות', statuses: ['DRAFT'] },
];

const SupplierPrograms = () => {
  const { showAlert, user } = useUser();

  // Creating a GEFEN program needs the platform's approval of this
  // learning center (supplier_profiles.status = 'APPROVED'). Anything
  // else — including an unknown status — keeps the button off; the
  // server enforces the same rule in ProgramsController::create().
  const supplierStatus = String(user?.supplier_status || '').toUpperCase();
  const canCreateProgram = supplierStatus === 'APPROVED';
  const createDisabledReason =
    supplierStatus === 'SUSPENDED'
      ? 'מרכז הלמידה מושעה — לא ניתן ליצור תוכנית'
      : 'לא ניתן ליצור תוכנית עד לאישור מרכז הלמידה על ידי הפלטפורמה';

  const { programs, loading, addProgram, updateProgram, archiveProgram, changeProgramStatus, fetchPrograms } =
    useSupplierPrograms();

  // The provider (wrapping the whole app) only auto-fetches once, tied
  // to login state changing — not tied to actually visiting this page.
  // By the time someone navigates here, that fetch may have already
  // finished long ago, somewhere else in the app, so `loading` would
  // already be false on first render here with no visible window for
  // the spinner at all. Re-fetching on mount gives this page its own
  // loading window every time it's actually visited.
  useEffect(() => {
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState('ALL');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, program: null });
  const [archiving, setArchiving] = useState(false);
  const [statusModalProgram, setStatusModalProgram] = useState(null);
  const [descriptionModalProgram, setDescriptionModalProgram] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const counts = useMemo(() => {
    const byKey = { ALL: programs.length };
    TAB_LIST.forEach((t) => {
      if (t.statuses) {
        byKey[t.key] = programs.filter((p) => t.statuses.includes(p.status)).length;
      }
    });
    return byKey;
  }, [programs]);

  const displayPrograms = useMemo(() => {
    const activeTab = TAB_LIST.find((t) => t.key === tab);
    if (!activeTab?.statuses) return programs;
    return programs.filter((p) => activeTab.statuses.includes(p.status));
  }, [programs, tab]);

  const handleCreateSubmit = async ({ payload }) => {
    // Server always forces status=DRAFT on create regardless of what's
    // sent — see ProgramsController::create(). Moving to PENDING/etc.
    // happens afterward from the card's 3-dot menu. CreateProgramModal
    // already catches/alerts on failure and shows its own success
    // toast, so this just makes the call.
    await addProgram(payload);
  };

  const handleEditSubmit = async ({ payload, id }) => {
    // `payload` never includes `status` — CreateProgramModal doesn't
    // touch it. Same as above: the modal owns success/error alerting.
    await updateProgram(id, payload);
  };

  const openConfirmArchive = (program) => setConfirm({ open: true, program });
  const closeConfirm = () => setConfirm({ open: false, program: null });

  const handleConfirmArchive = async () => {
    const program = confirm.program;
    if (!program) return closeConfirm();
    // Close the confirm dialog immediately, before the request starts —
    // otherwise it stays open for the whole request (closeConfirm was
    // only ever called in `finally`), stacked on top of/behind the
    // spinner below with no clear winner. Closing it first means only
    // the spinner is on screen while the request is actually running.
    closeConfirm();
    setArchiving(true);
    try {
      await archiveProgram(program.id);
      showAlert?.('התוכנית נסגרה', 'success');
    } catch (err) {
      showAlert?.(err.message || 'סגירת התוכנית נכשלה', 'error');
    } finally {
      setArchiving(false);
    }
  };

  const handleChangeStatus = async (program, newStatus) => {
    if (newStatus === program.status) return;
    // ChangeProgramStatusModal already catches/alerts on failure
    // itself (and stays open so the person can retry) — just make the
    // call and show the success toast; let errors propagate to it.
    await changeProgramStatus(program.id, newStatus);
    showAlert?.(`הסטטוס עודכן ל"${STATUS_LABELS[newStatus]}"`, 'success');
    setOpenMenuId(null);
  };


  return (
    <>
      <div className="min-h-screen bg-gray-100" dir="rtl">
        <SupplierProgramsHeader />

        <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 pt-2 pb-10 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex overflow-hidden rounded-lg bg-gray-50 p-1">
              {TAB_LIST.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                    tab === key ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {label} ({counts[key] ?? 0})
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => { if (canCreateProgram) setIsCreateOpen(true); }}
              disabled={!canCreateProgram}
              title={canCreateProgram ? 'תוכנית חדשה' : createDisabledReason}
              className={[
                'shrink-0 inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                canCreateProgram
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed',
              ].join(' ')}
            >
              <span className={`ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full ${canCreateProgram ? 'bg-white/15' : 'bg-black/10'}`}>
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <path d="M12 4v16M20 12H4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </span>
              תוכנית חדשה
            </button>
          </div>

          {!canCreateProgram && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {createDisabledReason}
            </div>
          )}

          {loading ? (
            <div className="relative min-h-[220px]">
              <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />
            </div>
          ) : displayPrograms.length === 0 ? (
            <div className="flex flex-col items-center bg-white rounded-2xl border-2 border-dashed border-gray-200 justify-center py-16 text-center">
              <div className="mb-4 w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">אין תוכניות בסטטוס זה</h3>
              <p className="text-sm text-gray-500 max-w-sm">צרו תוכנית חדשה כדי להתחיל.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayPrograms.map((program) => (
                <ProgramCard
                  key={program.id}
                  program={program}
                  isMenuOpen={openMenuId === program.id}
                  onMenuOpen={() => setOpenMenuId(program.id)}
                  onMenuClose={() => setOpenMenuId(null)}
                  onEdit={() => { setEditingProgram(program); setOpenMenuId(null); }}
                  onArchive={() => { openConfirmArchive(program); setOpenMenuId(null); }}
                  onOpenStatusModal={() => { setStatusModalProgram(program); setOpenMenuId(null); }}
                  onOpenDescription={() => setDescriptionModalProgram(program)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateProgramModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        mode="create"
        onSubmit={handleCreateSubmit}
      />

      <CreateProgramModal
        isOpen={!!editingProgram}
        onClose={() => setEditingProgram(null)}
        mode="edit"
        program={editingProgram}
        onSubmit={handleEditSubmit}
      />

      <ChangeProgramStatusModal
        isOpen={!!statusModalProgram}
        onClose={() => setStatusModalProgram(null)}
        program={statusModalProgram}
        onSubmit={(newStatus) => handleChangeStatus(statusModalProgram, newStatus)}
      />

      <ProgramDescriptionModal
        isOpen={!!descriptionModalProgram}
        onClose={() => setDescriptionModalProgram(null)}
        program={descriptionModalProgram}
      />

      <ConfirmDialog
        isOpen={confirm.open}
        onClose={closeConfirm}
        onConfirm={handleConfirmArchive}
        title="לאשר סגירת תוכנית?"
        body={
          confirm.program
            ? `האם לסגור את "${confirm.program.name}"? לאחר סגירה התוכנית לא תהיה זמינה עוד לרישום בתי ספר חדשים.`
            : ''
        }
        confirmText="סגור תוכנית"
        cancelText="ביטול"
        danger
      />

      {archiving && (
        <div className="fixed inset-0 z-[1000]">
          <SpinnerOverlay variant="glow" text="סוגר תוכנית..." dark={false} blur={false} />
        </div>
      )}
    </>
  );
};

/* ── Kebab (3-dot) popup menu ─────────────────────────────────────
   Positioned with createPortal + getBoundingClientRect, same technique
   as PortalSelect in CreateProgramModal.jsx, so it escapes the card's
   overflow and stays anchored on scroll/resize.
------------------------------------------------------------------- */
function CardMenu({ isOpen, onOpen, onClose, onEdit, onArchive, canArchive, onOpenStatusModal }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const measure = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    measure();
    let ticking = false;
    const onWin = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { measure(); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const outside = (e) => {
      if (!btnRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        ref={btnRef}
        onClick={() => (isOpen ? onClose() : onOpen())}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="אפשרויות נוספות"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
      >
        <svg className="w-4.5 h-4.5" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="12" cy="19" r="1.8" />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            dir="rtl"
            style={{ position: 'absolute', top: coords.top, left: coords.left, zIndex: 1000000 }}
          >
            <div className="w-48 rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden py-1">
              <button
                type="button"
                onClick={onEdit}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition text-right"
              >
                <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                עריכה
              </button>

              <button
                type="button"
                onClick={onOpenStatusModal}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition text-right"
              >
                <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 11l3 3L22 4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
                שינוי סטטוס
              </button>

              <div className="my-1 border-t border-gray-100" />

              {canArchive && (
                <button
                  type="button"
                  onClick={onArchive}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition text-right"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 9.5l5 5m0-5l-5 5" />
                  </svg>
                  סגירה
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function ProgramCard({ program, isMenuOpen, onMenuOpen, onMenuClose, onEdit, onArchive, onOpenStatusModal, onOpenDescription }) {
  const canArchive = ['DRAFT', 'PENDING', 'APPROVED', 'ACTIVE'].includes(program.status);
  const hasDescription = !!(program.description && program.description.trim());
  const dot = STATUS_DOT_CLASS[program.status];

  return (
    <div className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-gray-300 transition-all duration-200 overflow-hidden">
      {/* Status accent bar */}
      <div className={`h-1 w-full ${STATUS_BAR_CLASS[program.status]}`} />

      <div className="p-4">
        {/* Header: badge + kebab on one line, title below (room to breathe / wrap) */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_BADGE_CLASS[program.status]}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
            {STATUS_LABELS[program.status]}
          </span>

          <CardMenu
            isOpen={isMenuOpen}
            onOpen={onMenuOpen}
            onClose={onMenuClose}
            onEdit={onEdit}
            onArchive={onArchive}
            canArchive={canArchive}
            onOpenStatusModal={onOpenStatusModal}
          />
        </div>

        <h3 className="font-bold text-gray-900 text-[15px] leading-snug mb-2.5 line-clamp-2">
          {program.name}
        </h3>

        {/* Meta chips: GEFEN number + audience */}
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-1 font-mono">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h10M7 12h10M7 17h6" />
            </svg>
            {program.gefen_program_number || 'ללא מספר תוכנית'}
          </span>

          {program.target_audience && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 bg-indigo-50 rounded-md px-2 py-1">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20v-1a4 4 0 00-4-4H7a4 4 0 00-4 4v1" />
                <circle cx="10" cy="7" r="3.5" />
              </svg>
              {program.target_audience}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center rounded-xl bg-gray-50 divide-x divide-x-reverse divide-gray-200 mb-3">
          <StatCell value={program.schools} label="בתי ספר" icon={<SchoolIcon />} />
          <StatCell value={program.teachers} label="מורים" icon={<TeacherIcon />} />
          <StatCell value={program.students} label="תלמידים" icon={<StudentIcon />} />
        </div>

        {/* Description */}
        <button
          type="button"
          onClick={() => hasDescription && onOpenDescription?.()}
          disabled={!hasDescription}
          className={`w-full flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-semibold transition ${
            hasDescription
              ? 'text-indigo-600 bg-indigo-50/70 hover:bg-indigo-50'
              : 'text-gray-300 bg-gray-50 cursor-not-allowed'
          }`}
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6M8.5 4H16a2 2 0 012 2v12a2 2 0 01-2 2H8a2 2 0 01-2-2V6.5L8.5 4z" />
          </svg>
          {hasDescription ? 'הצג תיאור' : 'אין תיאור לתוכנית'}
        </button>
      </div>
    </div>
  );
}

function StatCell({ value, label, icon }) {
  return (
    <div className="flex-1 flex flex-col items-center gap-0.5 py-2.5">
      <div className="text-gray-400 mb-0.5">{icon}</div>
      <div className="text-sm font-bold text-gray-900 leading-none">{value}</div>
      <div className="text-[10.5px] text-gray-400">{label}</div>
    </div>
  );
}

function SchoolIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l9 5-9 5-9-5 9-5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10.5V16c0 1.5 3 3 7 3s7-1.5 7-3v-5.5" />
    </svg>
  );
}

function TeacherIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <circle cx="12" cy="8" r="3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.5 20a6.5 6.5 0 0113 0" />
    </svg>
  );
}

function StudentIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 12l8-4 8 4-8 4-8-4z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v4c0 1.1 1.79 2 4 2s4-.9 4-2v-4" />
    </svg>
  );
}

export default SupplierPrograms;